import React from 'react'

import FieldProto from "../../../fields3/proto/field-proto"
import Condition from "../../../model2/conditions/condition"
import FiltersManager from "./filters-manager"
import ItemModel from "../../../model2/item/item-model"
import FieldFilterForm from "../../../fields3/render/filter-form"
import filters_changeFilterCondition from "./actions/change-filter-condition"
import {ConditionType, IConditionType} from "../../../model2/conditions/condition-types"
import {FieldConditionRenderMode} from "../../../fields3/render/condition/@types"
import {FilterCollectionType, FilterItemModel} from "./@types"
import FieldsCollection from "../../../fields3/collection/base/fields-collection"
import FiltersManager_V2 from "./v2/filters-manager-v2"

// const debug = "Filter"
const debug = false

/**
 * Filter class represents a filter instance in version 5 of the filtering system.
 * It manages individual field filters with conditions and provides filtering functionality.
 */
export default class Filter extends Condition {

    /** Version number of the filter implementation */
    public version          : number = 5

    /** Reference to the parent filters manager that controls this filter */
    public filtersManager   : FiltersManager | FiltersManager_V2

    /**
     * Indicates whether the filter is currently loading data (e.g., fetching options for a field).
     * This can be used by the UI to show loading indicators for this filter row.
     */
    isDataLoading?          : boolean = false

    /**
     * Constructs a new Filter instance.
     * @param filtersManager - The parent FiltersManager instance that owns this filter.
     * @param field - The field definition for this filter (FieldProto).
     * @param initValue - (Optional) The initial value for the filter condition. If not provided, uses the first available condition type from the field.
     */
    constructor(filtersManager: FiltersManager | FiltersManager_V2, field: FieldProto, initValue?: any) {

        debug && console.log(debug, 'constructor', field.fieldId, _.cloneDeep(initValue), field)

        // If no initial value is provided, create a default one using the first condition type of the field
        if (!initValue) {
            initValue = {
                type: (field.defaultConditionType as typeof IConditionType)?.type
            }
        }

        super(initValue)

        // Store a reference to the parent filters manager
        this.filtersManager = filtersManager

        // Attempt to resolve the system field from the initial value (by systemFieldId)
        this._field         = this.getSystemField(initValue)

        // If the field is not set by initValue, assign it from the constructor parameter (for empty)
        if (!this.field) {
            this.field = field
        }

        debug && console.log(debug, 'constructor', this)
    }

    /**
     * Retrieves the system field definition based on the provided data.
     * This is typically used to resolve a field by its systemFieldId from the available fields collections.
     * @param data - An object that may contain a systemFieldId property.
     * @returns The matching FieldProto instance if found, otherwise null.
     */
    getSystemField(data: any): FieldProto | undefined {
        let field = undefined

        // Check if the data object contains a systemFieldId property
        if (!!data.systemFieldId) {
            // Iterate over all fields collections in the filters manager
            _.some(this.filtersManager?.fieldsCollections, (fieldsCollection: FieldsCollection) => {
                // Attempt to retrieve the field by its systemFieldId
                field = fieldsCollection.getBySystemFieldId(data.systemFieldId)
                // If a field is found, stop iterating further
                return !!field?.type
            })
        }

        return field
    }

    /**
     * Handles changes to the filter field
     * @param field - The new field definition
     */
    onChangeFilter = async (field: FieldProto)=> {
        this.field      = field

        debug && console.log(debug, 'onChangeFilter', this)

        await this.filtersManager.onChange(this)
    }

    /**
     * Handles changes to the filter condition
     * @param type - The type of condition
     * @param value - The new value for the condition
     */
    onConditionChange = (type:ConditionType, value: any) => {
        filters_changeFilterCondition(this, type, value)
        this.filtersManager.onChange()
    }

    /**
     * Removes this filter from the filters manager
     */
    remove() {
        this.filtersManager.removeFilter(this)
    }

    /**
     * Collects a set of unique values for the filter from the relevant collection.
     * 
     * This method uses the field's `filterValuesFiller` function to extract all possible
     * values from the items or models collection, depending on the current filter manager's type.
     * The result is a Set containing all unique values that can be used for filtering.
     * 
     * @returns {Promise<Set<any>>} A promise that resolves to a Set of unique values.
     */
    async collectValuesSet() {
        // Get the filter values filler function from field
        const filterValuesFiller = this.field!.filterValuesFiller

        // Initialize the values set and mark as loaded
        const valuesSet = new Set<any>()

        // If filter function exists, process items to extract valid values
        if (!!filterValuesFiller) {
            switch (this.filtersManager.collectionType) {
                case FilterCollectionType.ITEMS_COLLECTION:
                    // Iterate through all models in the items collection
                    _.map(this.filtersManager.itemsCollection?.models, (itemModel: ItemModel) => {
                        // Only process items that match the field's entity type
                        if (itemModel.type === this.field?.entityType) {
                            // Extract values using the filter function and add to set
                            _.map(filterValuesFiller(this, itemModel.item), (value: any) => {
                                valuesSet!.add(value)
                            })
                        }
                    })
                    break

                case FilterCollectionType.COLLECTION:
                    // Iterate through all models in the items collection
                    _.map(this.filtersManager.modelsCollection?.models, (model: any) => {
                        // Extract values using the filter function and add to set
                        _.map(filterValuesFiller(this, model), (value: any) => {
                            valuesSet!.add(value)
                        })
                    })
                    break
            }
        }

        /** --- Test purposes only [START] **/
        // Add an artificial async delay using setTimeout
        // await new Promise(resolve => setTimeout(resolve, 1000))
        /** --- Test purposes only [END] **/

        return valuesSet
    }

    /**
     * Filters a generic model using the field's filter function.
     * @param model - The model to be filtered (can be any type).
     * @returns {boolean} True if the model passes the filter, false otherwise.
     */
    filterModel(model: any): boolean {
        // Call the field's filterFunc, passing this filter and the model.
        // The result is coerced to a boolean.
        const res = !!this.field?.filterFunc(this, model)

        // Debug logging for filter operation
        debug && console.log(debug, 'filterModel', model, res, this.field)

        return res
    }

    /**
     * Filters an item model based on the current condition
     * @param itemModel - The item model to filter
     * @returns {boolean} True if the item passes the filter, false otherwise
     */
    filterItemModel(itemModel: FilterItemModel): boolean {
        const res = !!this.field?.filterItemModel(this, itemModel)

        debug && console.log(debug, 'filterItemModel', itemModel.debugName, res, this.field)

        return res
    }

    get renderMode() {
        switch (this.filtersManager.collectionType) {
            case FilterCollectionType.ITEMS_COLLECTION:
                return FieldConditionRenderMode.FILTER_ITEMS_COLLECTION

            case FilterCollectionType.COLLECTION:
                return FieldConditionRenderMode.FILTER_COLLECTION
        }
    }

    /**
     * Renders the filter form component
     * @returns A React component for the filter form
     */
    render() {
        return <FieldFilterForm renderMode          = {this.renderMode}
                                field               = {this.field!}
                                condition           = {this}
                                onChangeCallback    = {this.onConditionChange}/>
    }

    /**
     * Converts the filter to a plain object representation (for save)
     * @returns The filter condition as a JSON object
     */
    toObject() {
        return this.toJSON()
    }
}
